import { useEffect, useRef, useState } from 'react';

import { createPortal } from 'react-dom';

import {
  HelpCircleIcon,
  Logout02Icon,
  Settings02Icon,
  UserMultiple02Icon,
} from '@hugeicons/core-free-icons';
import { HugeiconsIcon } from '@hugeicons/react';
import cn from 'classnames';
import { Permissions } from 'constants/permissions';
import { useAccessControl } from 'hooks/access-control';
import { useClickOutside } from 'hooks/click-outside';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';

import { selectPermissions } from 'api/user-permissions-slice';

import styles from './ProfileCard.module.scss';

export const ProfileCard = ({
  user,
  visible = false,
  onClose = () => {},
  onSignOut = () => {},
  children,
  showViewAccount = true,
}) => {
  const wrapperRef = useRef(null);
  const [isMobile, setIsMobile] = useState(false);

  const permissions = useSelector(selectPermissions);
  const hasAccess = useAccessControl(permissions);

  useClickOutside(wrapperRef, onClose);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Prevent body scroll on mobile when card is visible
  useEffect(() => {
    if (isMobile && visible) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, visible]);

  const cardContent = (
    <div
      className={cn(
        styles.root,
        { [styles.rootHidden]: !visible },
        'shadow-2xl rounded-4xl shadow-soft border border-border-primary',
      )}
      ref={wrapperRef}>
      <div className="flex items-center gap-2">
        <div className="flex flex-col gap-1 p-6">
          <span className="font-semibold text-text-primary text-2xl">{user.name}</span>
          <span className="text-lg text-text-secondary">{user.email}</span>
          {showViewAccount && (
            <Link to="/account/profile" className={styles.accountBtn}>
              View Account
            </Link>
          )}
        </div>
      </div>
      {children && <hr />}
      {children && children}
      <hr />
      <div className={cn(styles.linksWrapper, 'p-6')}>
        {hasAccess(Permissions.CompanyWrite) && (
          <Link to="/company" className={styles.link}>
            <HugeiconsIcon icon={Settings02Icon} strokeWidth="2" />
            Company Settings
          </Link>
        )}
        {hasAccess(Permissions.CompanyWrite) && (
          <Link to="/users" className={styles.link}>
            <HugeiconsIcon icon={UserMultiple02Icon} strokeWidth="2" />
            User Access
          </Link>
        )}
        <a href="https://help.checkmate.tech/en/" target="_blank" className={styles.link}>
          <HugeiconsIcon icon={HelpCircleIcon} strokeWidth="2" />
          Support
        </a>
        <a className={styles.link} onClick={onSignOut}>
          <HugeiconsIcon icon={Logout02Icon} strokeWidth="2" />
          Sign Out
        </a>
      </div>
    </div>
  );

  // On mobile, render via portal with backdrop
  if (isMobile && visible) {
    return createPortal(
      <>
        <div className={styles.backdrop} onClick={onClose} />
        {cardContent}
      </>,
      document.body,
    );
  }

  // On desktop, render normally
  return cardContent;
};
